import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit, Plus, Search, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { api } from '@/services/api';

interface Branch {
  id: string;
  businessId: string;
  name: string;
  location?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const BranchesPage: React.FC = () => {
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editing, setEditing] = useState<Branch | null>(null);

  const [form, setForm] = useState({
    name: '',
    location: '',
    isActive: true,
  });

  const { data, isLoading, isError } = useQuery<{ branches: Branch[]; total: number }>({
    queryKey: ['branches', showInactive],
    queryFn: async () => {
      const res = await api.get(`/api/branches?includeInactive=${showInactive ? 'true' : 'false'}`);
      const branches = res.data?.data?.branches || res.data?.branches || res.data || [];
      return { branches, total: branches.length };
    },
  });

  const branches = data?.branches || [];

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return branches;
    return branches.filter((b: Branch) => {
      return (
        (b.name || '').toLowerCase().includes(q) ||
        (b.location || '').toLowerCase().includes(q) ||
        (b.id || '').toLowerCase().includes(q)
      );
    });
  }, [branches, searchTerm]);

  const resetForm = () => {
    setForm({ name: '', location: '', isActive: true });
  };

  const openCreate = () => {
    resetForm();
    setIsCreateOpen(true);
  };

  const openEdit = (b: Branch) => {
    setEditing(b);
    setForm({ name: b.name || '', location: b.location || '', isActive: b.isActive !== false });
    setIsEditOpen(true);
  };

  const closeModals = () => {
    setIsCreateOpen(false);
    setIsEditOpen(false);
    setEditing(null);
    resetForm();
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: form.name.trim(),
        location: form.location.trim() || undefined,
        isActive: form.isActive,
      };
      const res = await api.post('/api/branches', payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      toast.success('Branch created');
      closeModals();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create branch');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editing) throw new Error('No branch selected');
      const payload = {
        name: form.name.trim(),
        location: form.location.trim() || undefined,
        isActive: form.isActive,
      };
      const res = await api.put(`/api/branches/${editing.id}`, payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      toast.success('Branch updated');
      closeModals();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update branch');
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/branches/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      toast.success('Branch deactivated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to deactivate branch');
    },
  });

  const canSave = form.name.trim().length > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Branches / Warehouses</h1>
          <p className="mt-1 text-sm text-gray-600">Manage stock locations (warehouses)</p>
        </div>

        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          New Branch
        </Button>
      </div>

      <div className="card">
        <div className="card-body space-y-4">
          <Input
            placeholder="Search branches..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={<Search className="h-4 w-4 text-gray-400" />}
          />

          <label className="flex items-center space-x-2 text-sm text-gray-700">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            <span>Show inactive branches</span>
          </label>
        </div>
      </div>

      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">Branches</h2>
          <div className="text-sm text-gray-600">Total: {filtered.length}</div>
        </div>
        <div className="card-body">
          {isLoading ? (
            <div className="text-sm text-gray-600">Loading branches...</div>
          ) : isError ? (
            <div className="text-sm text-red-600">Failed to load branches</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-gray-600">No branches found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filtered.map((b: Branch) => (
                    <tr key={b.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{b.name}</div>
                        <div className="text-xs text-gray-500">{b.id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-700">{b.location || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {b.isActive !== false ? (
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right space-x-2">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(b)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deactivateMutation.mutate(b.id)}
                          disabled={deactivateMutation.isPending || b.isActive === false}
                          loading={deactivateMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {(isCreateOpen || isEditOpen) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">{isCreateOpen ? 'New Branch' : 'Edit Branch'}</h3>
              <button onClick={closeModals} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <Input
                label="Name"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Main Warehouse"
              />

              <Input
                label="Location"
                value={form.location}
                onChange={(e) => setForm((prev) => ({ ...prev, location: e.target.value }))}
                placeholder="e.g. Lilongwe"
              />

              <label className="flex items-center space-x-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                />
                <span>Active</span>
              </label>
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end space-x-2">
              <Button variant="secondary" onClick={closeModals}>
                Cancel
              </Button>
              <Button
                onClick={() => (isCreateOpen ? createMutation.mutate() : updateMutation.mutate())}
                disabled={!canSave}
                loading={createMutation.isPending || updateMutation.isPending}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BranchesPage;
